/**
 * Environment + brand configuration.
 *
 * Domains are placeholders that will change when branding is finalised; keep
 * them in one place so DNS, ACM and CloudFront configuration stay in sync.
 */
export type AppEnvironment = 'local' | 'dev' | 'stage' | 'prod';

export const APP_ENV: AppEnvironment = (import.meta.env.VITE_APP_ENV as AppEnvironment) ?? 'local';

export const IS_PRODUCTION = APP_ENV === 'prod';

export const site = {
  name: 'Agave',
  legalName: 'Agave Interactive',
  tagline: 'Desert-bloom casino nights, without the risk.',
  description:
    'Agave is a premium social casino — slots, table classics, provably-fair originals and live-style dealers. Play with virtual credits. No real-money wagering.',
  supportEmail: 'support@agave.cntnus.app',
  securityEmail: 'security@agave.cntnus.app',
  privacyEmail: 'privacy@agave.cntnus.app',
  domains: {
    local: 'http://localhost:5173',
    dev: 'https://dev.agave.cntnus.app',
    stage: 'https://stage.agave.cntnus.app',
    prod: 'https://agave.cntnus.app',
  } satisfies Record<AppEnvironment, string>,
  socials: {
    x: 'https://x.com/agavecassino',
    discord: 'https://discord.gg/agave',
    telegram: 'https://t.me/agave',
  },
  /** Regulatory posture — this build is a social/sweepstakes-style product. */
  realMoney: false,
  minimumAge: 18,
} as const;

export const currentDomain = site.domains[APP_ENV];

export const build = {
  commit: import.meta.env.VITE_COMMIT_SHA ?? 'dev',
  time: import.meta.env.VITE_BUILD_TIME ?? new Date().toISOString(),
  environment: APP_ENV,
} as const;

export const nav = {
  primary: [
    { label: 'Casino', to: '/casino' },
    { label: 'Slots', to: '/casino/slots' },
    { label: 'Originals', to: '/casino/originals' },
    { label: 'Table', to: '/casino/table' },
    { label: 'Live', to: '/casino/live' },
    { label: 'Promotions', to: '/promotions' },
    { label: 'VIP', to: '/vip' },
  ],
  footer: {
    casino: [
      { label: 'All games', to: '/casino' },
      { label: 'Slots', to: '/casino/slots' },
      { label: 'Table games', to: '/casino/table' },
      { label: 'Originals', to: '/casino/originals' },
      { label: 'Live dealers', to: '/casino/live' },
    ],
    account: [
      { label: 'Wallet', to: '/wallet' },
      { label: 'Account', to: '/account' },
      { label: 'Responsible gaming', to: '/responsible-gaming' },
      { label: 'Help centre', to: '/help' },
    ],
    legal: [
      { label: 'Terms of service', to: '/legal/terms' },
      { label: 'Privacy policy', to: '/legal/privacy' },
      { label: 'Cookie policy', to: '/legal/cookies' },
      { label: 'Provably fair', to: '/legal/fairness' },
      { label: 'Security', to: '/legal/security' },
      { label: 'Accessibility', to: '/legal/accessibility' },
    ],
  },
} as const;
